// ═══ SRT Parser — Proven from Sahaja + Bianca ═══

import type { SRTEntry, LyricBlock } from './types'

/**
 * Parse an SRT string into entries.
 * Handles standard SRT format with HH:MM:SS,mmm timestamps.
 */
export function parseSRT(raw: string): SRTEntry[] {
  const entries: SRTEntry[] = []
  const blocks = raw.trim().split(/\n\n+/)

  for (const block of blocks) {
    const lines = block.split('\n')
    if (lines.length < 3) continue

    const index = parseInt(lines[0].trim(), 10)
    if (isNaN(index)) continue

    const m = lines[1].match(
      /(\d{2}):(\d{2}):(\d{2}),(\d{3})\s*-->\s*(\d{2}):(\d{2}):(\d{2}),(\d{3})/
    )
    if (!m) continue

    const startMs = (+m[1] * 3600 + +m[2] * 60 + +m[3]) * 1000 + +m[4]
    const endMs = (+m[5] * 3600 + +m[6] * 60 + +m[7]) * 1000 + +m[8]
    const text = lines.slice(2).join(' ').trim()

    entries.push({ index, startMs, endMs, text })
  }

  return entries
}

/**
 * Convert SRT entries to LyricBlocks for the editor.
 */
export function srtToLyricBlocks(entries: SRTEntry[]): LyricBlock[] {
  return entries.map((entry) => ({
    id: `lyric-${entry.index}`,
    index: entry.index,
    startMs: entry.startMs,
    endMs: entry.endMs,
    text: entry.text,
    mode: 'line' as const,
    isPowerWord: false,
  }))
}

/**
 * Export LyricBlocks back to SRT format.
 */
export function lyricBlocksToSRT(blocks: LyricBlock[]): string {
  const sorted = [...blocks].sort((a, b) => a.startMs - b.startMs)
  return sorted.map((block, i) => {
    const idx = i + 1
    const start = msToTimestamp(block.startMs)
    const end = msToTimestamp(block.endMs)
    return `${idx}\n${start} --> ${end}\n${block.text}`
  }).join('\n\n') + '\n'
}

/**
 * Split a lyric block into individual words (for power word isolation).
 * Each word gets proportional timing within the original block's time range.
 */
export function splitBlockToWords(block: LyricBlock): LyricBlock[] {
  const words = block.text.split(/\s+/).filter(w => w.length > 0)
  if (words.length <= 1) return [block]

  const totalMs = block.endMs - block.startMs
  const msPerWord = totalMs / words.length

  return words.map((word, i) => ({
    id: `${block.id}-w${i}`,
    index: block.index * 100 + i, // Preserve ordering
    startMs: Math.round(block.startMs + i * msPerWord),
    endMs: Math.round(block.startMs + (i + 1) * msPerWord),
    text: word,
    sectionId: block.sectionId,
    mode: 'word' as const,
    isPowerWord: false,
    style: block.style,
  }))
}

/**
 * Merge consecutive lyric blocks back into one.
 */
export function mergeBlocks(blocks: LyricBlock[]): LyricBlock {
  const sorted = [...blocks].sort((a, b) => a.startMs - b.startMs)
  return {
    id: sorted[0].id,
    index: sorted[0].index,
    startMs: sorted[0].startMs,
    endMs: sorted[sorted.length - 1].endMs,
    text: sorted.map(b => b.text).join(' '),
    sectionId: sorted[0].sectionId,
    mode: 'line',
    isPowerWord: false,
    style: sorted[0].style,
  }
}

// ═══ Helpers ═══

function msToTimestamp(ms: number): string {
  const hours = Math.floor(ms / 3600000)
  const minutes = Math.floor((ms % 3600000) / 60000)
  const seconds = Math.floor((ms % 60000) / 1000)
  const millis = ms % 1000
  return `${pad2(hours)}:${pad2(minutes)}:${pad2(seconds)},${pad3(millis)}`
}

function pad2(n: number): string {
  return n.toString().padStart(2, '0')
}

function pad3(n: number): string {
  return n.toString().padStart(3, '0')
}
